import classNames from 'classnames'
import { Fragment, JSX, useCallback, useMemo } from 'react'

import { Checkbox } from 'core-components/library/checkbox'
import { Tooltip } from 'core-components/library/tooltip'

import { getKeyField } from '../../helpers'
import styles from '../../Table.module.scss'
import { Actions } from '../Actions'
import { calcColumnsToRender } from '../helpers/calcColumns'
import { prepareStickyColumns } from '../helpers/prepareStickyColumns'
import stylesStickyColumns from '../StickyColumns.module.scss'
import {
  ICellAttrs,
  ICellAttrsStorage,
  IColumn,
  ICustomColumnOptions,
  RowState,
  TableMode,
  TplFunction,
  TTableProps,
} from '../types'

export function useTemplates<P, R>(props: TTableProps<P, R>) {
  const keyField = props.keyField || props.source?.keyField || 'EntryId'

  const renderedColumn = useMemo(() => {
    return calcColumnsToRender<P>(props.columns, keyField)
  }, [props.columns, keyField])

  const hierarchyTpl = useCallback(
    (
      item: P,
      rowState: RowState,
      hierarchyClick: (item: P) => void,
      hierarchyContent: (
        item: P,
        rowState: RowState,
        hierarchyClick: (item: P) => void
      ) => JSX.Element,
      cellAttrs?: ICellAttrs
    ) => {
      return (
        <>
          {props.mode === TableMode.div ? (
            <div className={classNames(styles.hierarchyCell, styles['hierarchyCell--div'])}>
              {hierarchyContent(item, rowState, hierarchyClick)}
            </div>
          ) : (
            <td
              className={classNames(styles.hierarchyCell, cellAttrs?.className)}
              style={{ ...cellAttrs?.style }}
            >
              {hierarchyContent(item, rowState, hierarchyClick)}
            </td>
          )}
        </>
      )
    },
    [props.mode]
  )

  const rowTpl: TplFunction<P> = useCallback(
    (item, renderChildren, rowState, rowAttrs, additional, focusedId, onCheck) => {
      const hasStickyColumns = props.columns.some((item) => item.isStickyColumn)
      const { key, testId, deep, index, ...rowAttrsRest } = rowAttrs

      const isRowDisabled = props.getRowCheckboxDisabled?.(item) ?? false

      if (props.mode === TableMode.div) {
        return (
          <div
            key={key}
            {...rowAttrsRest}
            data-testid={testId}
            className={classNames(styles.divRow, rowAttrs.className)}
            style={{
              paddingLeft: deep ? `${deep * 16}px` : undefined,
            }}
          >
            {props.useRowCheckbox && (
              <Checkbox
                __testId={`table-row-${item[keyField]}-checkbox`}
                indeterminate={rowState.indeterminate}
                checked={rowState.selected}
                disabled={isRowDisabled}
                onChange={(e) => {
                  onCheck && onCheck(item, e)
                }}
              />
            )}
            {renderChildren(item, rowState, additional, focusedId, rowAttrs)}
          </div>
        )
      }

      return (
        <tr
          {...rowAttrsRest}
          data-testid={testId}
          key={key}
          className={classNames(styles.row, rowAttrs.className, {
            [styles.row__withSticky]: hasStickyColumns,
            [styles.viewRow_withSticky]: hasStickyColumns,
            [styles.viewRow_selectedWithSticky]: hasStickyColumns,
          })}
        >
          {props.useRowCheckbox && (
            <td className={styles.cell}>
              <Checkbox
                __testId={`table-row-${item[keyField]}-checkbox`}
                className={styles.checkBox}
                indeterminate={rowState.indeterminate}
                checked={rowState.selected}
                disabled={isRowDisabled}
                onChange={(e) => {
                  if (onCheck) {
                    onCheck(item, e)
                  }
                }}
              />
            </td>
          )}
          {renderChildren(item, rowState, additional, focusedId, rowAttrs)}
        </tr>
      )
    },
    [props.columns, props.getRowCheckboxDisabled, props.mode, props.useRowCheckbox, keyField]
  )

  const columnTpl = useMemo(() => {
    const basicTpl = (
      item: P,
      column: IColumn<P>,
      cellAttrs: any,
      rowState: any,
      focusedId?: any,
      rowAttrs?: any
    ) => {
      return (
        <Fragment key={cellAttrs.key}>
          {props.mode === TableMode.div ? (
            <div {...cellAttrs} key={cellAttrs.key} className={styles.divCell}>
              {column.renderFunction
                ? column.renderFunction(item, column, rowAttrs)
                : item['get']
                  ? item['get'](column.field)
                  : item[column.field]}
            </div>
          ) : (
            <td
              {...cellAttrs}
              key={cellAttrs.key}
              className={classNames(styles.cell, cellAttrs.className, column.className)}
              style={{
                width: typeof column.width === 'number' ? `${column.width}px` : column.width,
                minWidth: typeof column.width === 'number' ? `${column.width}px` : column.width,
                maxWidth: column.width ? 0 : undefined,
                ...cellAttrs.style,
              }}
            >
              {column.renderFunction ? (
                column.renderFunction(item, column, rowAttrs)
              ) : !column.width ? (
                item['get'] ? (
                  item['get'](column.field)
                ) : (
                  item[column.field]
                )
              ) : (
                <Tooltip
                  tipContent={() => (
                    <>{item['get'] ? item['get'](column.field) : item[column.field]}</>
                  )}
                >
                  <div className={styles.ellipsis}>
                    {item['get'] ? item['get'](column.field) : item[column.field]}
                  </div>
                </Tooltip>
              )}
            </td>
          )}
        </Fragment>
      )
    }

    return (item: P, rowState: RowState, additional: any, focusedId: any, rowAttrs?: any) => {
      const hasStickyColumns = props.columns.some((item) => item.isStickyColumn)

      const cellClassNames = classNames({
        [stylesStickyColumns.stickyColumn]: hasStickyColumns,
      })

      const key: string = rowAttrs?.key

      const newCellAttrs: ICellAttrsStorage = {
        ...rowState.cellAttrs,
        [key]: {
          key,
          className: cellClassNames,
          style: {},
        },
      }
      if (hasStickyColumns) {
        newCellAttrs[key].style = { left: 0 }
      }
      const hierarchyTemplate = props.hierarchyTpl || hierarchyTpl
      const renderCells = () => {
        const fn = props.cellTpl || basicTpl
        const cellAttrs = rowState.cellAttrs || {}
        const drawColumn = (opt: { index: number; columnOptions?: ICustomColumnOptions }) => {
          const key = `cell-${opt.index}`
          const currentColumn = renderedColumn[opt.index]
          const { leftColumnShift, isLastStickyColumn } = prepareStickyColumns(
            currentColumn,
            props.columns,
            props.hasHierarchy
          )

          const cellClassNames = classNames({
            [stylesStickyColumns.stickyColumn]: currentColumn.isStickyColumn,
            [stylesStickyColumns.stickyColumn__lastStickyColumn]: isLastStickyColumn,
          })

          const newCellAttrs: ICellAttrsStorage = {
            ...cellAttrs,
            [key]: {
              key,
              ...(opt.columnOptions || {}),
              className: classNames(cellClassNames, opt.columnOptions?.className),
              style: {
                ...(opt.columnOptions?.style || {}),
              },
            },
          }

          if (currentColumn.isStickyColumn) {
            newCellAttrs[key].style = {
              left: leftColumnShift,
              ...(opt.columnOptions?.style || {}),
            }
          }

          return fn(item, currentColumn, newCellAttrs[key], rowState, focusedId, rowAttrs)
        }
        if (props.columnIterator) {
          return props.columnIterator({
            iterator: (options) => {
              return drawColumn(options)
            },
          })
        }
        return renderedColumn.map((_, index) => {
          return drawColumn({ index })
        })
      }

      return (
        <Fragment>
          {props.hasHierarchy &&
            hierarchyTemplate(
              item,
              rowState,
              additional.hierarchyClick,
              additional.hierarchyContent,
              newCellAttrs[key]
            )}
          {renderCells()}
          {props.rowActions ? (
            <>
              {props.mode === TableMode.div ? (
                <Actions<P>
                  item={item}
                  actions={props.rowActions}
                  keyField={getKeyField({ source: props.source, keyField: props.keyField })}
                  columns={props.columns}
                />
              ) : (
                <td className={styles.actionCell}>
                  <Actions<P>
                    item={item}
                    actions={props.rowActions}
                    keyField={getKeyField({ source: props.source, keyField: props.keyField })}
                    columns={props.columns}
                  />
                </td>
              )}
            </>
          ) : (
            <></>
          )}
        </Fragment>
      )
    }
  }, [props, hierarchyTpl, renderedColumn])

  return {
    rowTpl: props.rowTpl || rowTpl,
    columnTpl,
  }
}
